import { Advantage } from './advantages.types';
import { DeliveryOptionsEnum } from './DeliveryPromise';
import {
  ProductTypes,
  BasketUpdateStatus,
  BasketResponseCode,
  AppliedVoucherTypes,
  StockLocation,
  BasketCartItemError,
  DynamicMessagesCalculateTypes,
  BasketStatus,
} from './enums';
import {
  HelpCenterProps,
  ProductColorVariationSize,
  ProductDeliveryPromiseExplanations,
  ProductPriceInformationWithDiscount,
  ProductPrices,
  ProductTileVerticalFormat,
} from './product-detail';
import { ProductLister } from './product-lister';

type BasketProductFeature = 'ecocheques_accepted';

export type BasketPromotion = {
  minimumBasketSize: number;
  recommendedProduct: BasketRequestObject;
};

export type BasketProductData = {
  sku: number;
  productId: number;
  productCode: string;
  colorId: number;
  quantity: number;
  ccExcluded: boolean;
  sseProduct?: boolean;
  specialtyStoreProduct?: boolean;
  availableInSpecialtyStore?: boolean;
  availableInSSEStore?: boolean;
  productPriceSell: number;
  productPriceStandard: number;
  totalProductPriceSell: number;
  totalProductPriceStandard?: number;
  productPriceManual?: number;
  productType: ProductTypes;
  additionalProperties: BasketProductDataAdditionalProperties;
  productDiscountPercentage: number;
  productDiscountAmount: number;
  prices: ProductPrices;
  totalProductDiscountAmount: number;
  promotion?: BasketPromotion;
  features: Array<BasketProductFeature>;
};

export type BasketPopupData = BasketStatusUpdateData & {
  basketProductData: BasketProductTileData | Array<BasketProductTileData>;
  price?: ProductPriceInformationWithDiscount;
};

export type BasketStatusUpdateData = {
  status: BasketUpdateStatus;
  errorCode?: BasketResponseCode;
  sku?: number;
  isBulk?: boolean;
};

export type BasketSetActionData = {
  status: BasketStatus;
  products?: Array<BasketProductData>;
};

export type BasketAppliedVoucher = {
  amountOriginal: number;
  amountUnused: number;
  amountUsedForThisBasket: number;
  barcode: string;
  description?: string;
  displayCode: string;
  message: string;
  minAmount?: number;
  printable?: boolean;
  showAmount: boolean;
  showValidate?: boolean;
  singleUse: boolean;
  type: AppliedVoucherTypes;
  valid: boolean;
  validFrom: Date | string;
  validTo: Date | string;
};

export type BasketPopupMessagesEnriched = {
  message: string;
  productCode?: string;
};

export type BasketPaybackVoucherMessage = {
  minAmount: number;
  discountAmount: number;
  amountUntilValid: number;
  currency: string;
  hasCustomMessage: boolean;
  customMessage: string;
};

export type BasketDeliveryMessages = {
  [key in DeliveryOptionsEnum]?: string;
};

export type BasketGetResponse = {
  basketId: string;
  market: string;
  mainWebshop: string;
  mainWebshopId: number;
  faciaId: number;
  fictionalId: number;
  username?: string;
  dateCreated: Date | string;
  customerUUID: string;
  products: Array<BasketProductData>;
  totalPriceSell: number;
  totalQuantity: number;
  currency: string;
  delivery: string;
  deliveryUnit: string;
  language: string;
  physicalShopId?: string | number;
  clickAndCollectStoreId?: number;
  hasDeliveryMessage: boolean;
  deliveryMessage: string;
  deliveryDateTo: Date | string;
  deliveryPromiseCutoff: string | null;
  priceDelivery: number;
  priceTotalBasket: number;
  amountUntilFreeDelivery?: number;
  registeredDuringCheckout: boolean;
  requiresDelivery: boolean;
  hasEvoucher: boolean;
  vouchers: Array<BasketAppliedVoucher>;
  orderId: string;
  orderReference: string;
  readyForCheckout: boolean;
  dateUserInteraction: Date | string;
  currentDate: Date | string;
  businessUnitId: number;
  validateRegex: string;
  popupMessages: Array<string>;
  popupMessagesEnriched: Array<BasketPopupMessagesEnriched>;
  additionalDeliveryMessages?: string;
  paybackVoucherMessages: Array<string>;
  deliveryMessages: BasketDeliveryMessages;
  totalBasketDiscountAmount: number;
  totalPriceSellBeforeDiscount: number;
  orderLinesHaveDifferentDeliveryPromises: boolean;
  totalVoucherAmount?: number;
  totalPendingPaymentAmount: number;
  priceTotalBasketMinusVouchers: number;
  locked: boolean;
};

export type BasketGetBasicResponse = {
  basketId: string;
  market?: string;
  mainWebshop?: string;
  mainWebshopId?: number;
  faciaId?: number;
  fictionalId?: number;
  username?: string;
  dateCreated?: Date | string;
  customerUUID?: string;
  products?: Array<BasketProductData>;
  totalPriceSell?: number;
  totalPriceSellBeforeDiscount?: number;
  totalQuantity: number;
  currency?: string;
  delivery?: string;
  deliveryUnit?: string;
  language?: string;
  physicalShopId?: string | number;
  hasDeliveryMessage: boolean;
  deliveryMessage?: string;
  deliveryDateTo?: Date | string;
  priceDelivery?: number;
  priceTotalBasket?: number;
  amountUntilFreeDelivery?: number;
  registeredDuringCheckout: boolean;
  requiresDelivery: boolean;
  hasEvoucher: boolean;
  vouchers?: Array<BasketAppliedVoucher>;
  orderId?: string;
  orderReference?: string;
  readyForCheckout: boolean;
  dateUserInteraction: Date | string;
  currentDate: Date | string;
  businessUnitId?: number;
  totalBasketDiscountAmount?: number;
};

export type BasketPopoverVoucher = {
  displayCode: string;
  amount: number;
};

export type BasketAddVoucherObject = {
  voucherSizeCode: string;
  amount: number;
  quantity: number;
  message: string;
  image: string;
  imageTemplateName: string;
};

export type BasketAddPromotionObject = BasketRequestObject;

export type BasketAddGiftCardObject = {
  giftCardSizeCode: string;
  amount: number;
  quantity: number;
};

export type BasketAddExtensiveTileProductObject = {
  product: ProductLister;
  colorId: number;
  sku: string;
  hideBasketPopup?: boolean;
};

export type BasketDeliveryInfo = {
  amountUntilFreeDelivery: number | null;
  delivery: string;
  deliveryMessages: BasketDeliveryMessages;
  deliveryUnit: string;
  hasEvoucher: boolean;
  requiresDelivery: boolean;
  deliveryPromiseCutoff?: string;
  hasDeliveryMessage?: boolean;
};

export type BasketPricing = {
  priceDelivery: number;
  priceTotalBasket: number;
  totalBasketDiscountAmount: number;
  totalPriceSell: number;
  totalPriceSellBeforeDiscount: number;
  totalPendingPaymentAmount?: number;
  priceTotalBasketMinusVouchers?: number;
};

export type BasketAddProductObject = {
  product: ProductLister;
  colorId: number;
  sku: string;
  quantity: number;
  isSelected?: boolean;
};

export type BasketRequestObject = {
  colorId: number;
  productCode: string;
  productId: string;
  quantity: number;
  sku: string;
  productPriceManual?: number;
  additionalProperties?: BasketProductDataAdditionalProperties;
};

export type BasketResponseObject = {
  amountUnableToReserve: number;
  availableQuantity: number;
  productId: number;
  requestedQuantity: number;
  skuId: number;
  success: boolean;
};

export type BasketProductDataAdditionalProperties = {
  deliveryMessage?: string;
  deliveryOptionName?: string;
  deliveryPromiseCutoff?: string;
  eVoucherId?: string;
  hasDeliveryMessage?: boolean;
  image?: string;
  layout?: string;
  message?: string;
  stockLocation?: StockLocation;
};

export type BasketProductTileData = {
  productCode: string;
  description: string;
  image: string;
  quantity: number;
  productId?: string;
  additionalImage?: string;
  availableInSSEStore?: boolean;
  availableInSpecialtyStore?: boolean;
  sseProduct?: boolean;
  specialtyStoreProduct?: boolean;
  ccExcluded?: boolean;
  hasWarehouseStock?: boolean;
  brand?: string;
  color?: string;
  colorId?: number;
  eVoucherId?: string;
  price?: number;
  priceManual?: number;
  previousPrice?: number;
  lowestPrice?: number;
  totalPrice?: number;
  totalPreviousPrice?: number;
  productType?: ProductTypes;
  seoUrl?: string;
  sizeDescription?: string;
  originalSize?: string;
  colorDescription?: string;
  swatch?: string;
  sku?: number;
  productHasDiscount?: boolean;
  productDiscountPercentage?: number;
  productDiscountAmount?: number;
  message?: string;
  sizes?: Array<ProductColorVariationSize>;
  errorState?: BasketCartItemError;
  additionalProperties?: BasketProductDataAdditionalProperties;
  prices?: ProductPrices;
  totalProductDiscountAmount?: number;
  success?: boolean;
  promotion?: BasketPromotion;
  features?: Array<BasketProductFeature>;
};

export type TotalMiniBlock = {
  deliveryTimeText: string;
  termsUrl: string;
  isVoucherCodeCollapsedOnDesktop: boolean;
  isVoucherCodeCollapsedOnMobileTablet: boolean;
};

export type DynamicMessages = {
  calculateBy: DynamicMessagesCalculateTypes;
  includeVouchers: boolean;
  ignoreGiftOrEvouchers: boolean;
  messages: Array<DynamicMessageItem>;
};

export type DynamicMessageItem = {
  minimumValue: number;
  maximumValue: number;
  message: string;
  startDate?: string;
  stopDate?: string;
};

export type BasketMenuItemContainerProps = {
  basketOverviewLink: string;
  checkoutLink: string;
  freeDeliveryMessage?: string;
  enableBasketReminderNotification: boolean;
  enableBasketReminderNotificationABTest: boolean;
};

export type BasketOverviewContainerOwnProps = {
  helpCenter: HelpCenterProps;
  advantages?: Array<Advantage>;
  brandLogoTimestamp?: number;
  recommendations?: {
    emptyBasketTitle?: string;
    scheme?: string;
    actionId?: string;
    tileFormat: ProductTileVerticalFormat;
    title?: string;
  };
  deliveryPromiseExplanations: ProductDeliveryPromiseExplanations;
  totalMiniBlock?: TotalMiniBlock;
  dynamicMessages?: DynamicMessages;
  fredhopper?: {
    tileFormat: ProductTileVerticalFormat;
    titleWithDeliveryCosts?: string;
    titleWithoutDeliveryCosts?: string;
  };
  freeDeliveryMessage?: string;
  general: {
    mobileNDDLocation: 'topCheckoutButton' | 'topBasketPage' | 'both';
    showMultilineDeliveryPromise: boolean;
  };
  preSolden?: {
    presoldenMessage: string;
  };
  mainWebShopId: number;
};
